import {Component, OnInit} from '@angular/core';
import {ToasterService} from 'angular2-toaster';
import {TranslocoService} from '@jsverse/transloco';
import {DataService} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';

interface MailSite {
    site: string;
    label: string;
    baseUrl: string;
}

interface MailTemplate {
    rid: string;
    key: string;
    site: string;
    subject: string;
    body: string;
}

/**
 * システム管理 > メール設定 (issue #171)。システム管理者のみ。
 *
 * <p>SMTP 設定は専用 API ({@code /rest/promotion/mail/setting}) で読み書きする。パスワードはサーバ側で
 * AES-GCM 暗号化して保存され、応答には出てこない (設定済みかどうかだけ分かる) ので、この画面でも
 * 現在値は表示せず、入力があったときだけ送る。
 *
 * <p>backend は 4 アプリ共有なので、リセットリンクのホストはアプリ (site) ごとに登録する。
 * 本文テンプレートは秘密ではないので、他のマスタと同じく汎用データ API で編集する。
 * {@code site} が空の行が共通で、アプリ名を入れた行があればそのアプリだけそちらが使われる。
 */
@Component({
    standalone: false,
    selector: 'app-system-mail',
    templateUrl: './system-mail.component.html',
})
export class SystemMailComponent implements OnInit {
    setting: any = {
        host: '', port: 587, username: '', fromAddress: '', fromName: '', security: 'starttls',
        // 送信済みフォルダへの保存 (IMAP)。認証は SMTP と同じアカウントを使う
        saveToSent: false, imapHost: '', imapPort: 993, imapSecurity: 'ssl', sentFolder: 'Sent'
    };
    /** 入力があったときだけ送る。現在値はサーバから取得できない */
    password = '';
    hasPassword = false;
    /** backend に AP_MAIL_SECRET が設定されているか。false なら保存も送信もできない */
    secretConfigured = true;
    saving = false;

    sites: MailSite[] = [];
    testTo = '';
    testing = false;

    templates: MailTemplate[] = [];
    template: MailTemplate | null = null;
    editing = false;

    constructor(private dataService: DataService, private toasterService: ToasterService, private transloco: TranslocoService) {
    }

    /** 表示時点の言語で訳す (トースト用)。 */
    private t(key: string, params?: any): string {
        return this.transloco.translate(key, params);
    }

    ngOnInit(): void {
        this.loadSetting();
        this.loadSites();
        this.loadTemplates();
    }

    // --- SMTP 設定 -------------------------------------------------------

    loadSetting(): void {
        this.dataService.send('GET', {}, ActivePromotionConfig.SERVER_URL + '/rest/promotion/mail/setting').subscribe(
            (res: any) => {
                if (res) {
                    this.setting = {
                        host: res.host || '',
                        port: res.port || 587,
                        username: res.username || '',
                        fromAddress: res.fromAddress || '',
                        fromName: res.fromName || '',
                        security: res.security || 'starttls',
                        saveToSent: res.saveToSent === true,
                        imapHost: res.imapHost || '',
                        imapPort: res.imapPort || 993,
                        imapSecurity: res.imapSecurity || 'ssl',
                        sentFolder: res.sentFolder || 'Sent'
                    };
                    this.hasPassword = res.hasPassword === true;
                    this.secretConfigured = res.secretConfigured !== false;
                }
            },
            () => this.toasterService.pop('error', this.t('route.mail_settings'), this.t('shell.mail_setting_load_failed'))
        );
    }

    saveSetting(): void {
        const body: any = Object.assign({}, this.setting);
        // 空のときは送らない = サーバ側の既存値を維持する
        if (this.password) {
            body.password = this.password;
        }
        this.saving = true;
        this.dataService.send('PUT', body, ActivePromotionConfig.SERVER_URL + '/rest/promotion/mail/setting').subscribe(
            (res: any) => {
                this.saving = false;
                this.password = '';
                if (res) {
                    this.hasPassword = res.hasPassword === true;
                    this.secretConfigured = res.secretConfigured !== false;
                }
                this.toasterService.pop('info', this.t('route.mail_settings'), this.t('shell.saved'));
            },
            (err: any) => {
                this.saving = false;
                this.toasterService.pop('error', this.t('route.mail_settings'), this.messageOf(err, this.t('shell.save_failed')));
            }
        );
    }

    /** 登録済みのパスワードを消す (認証なしの SMTP に戻すとき) */
    clearPassword(): void {
        const body: any = Object.assign({}, this.setting, {clearPassword: true});
        this.saving = true;
        this.dataService.send('PUT', body, ActivePromotionConfig.SERVER_URL + '/rest/promotion/mail/setting').subscribe(
            (res: any) => {
                this.saving = false;
                this.hasPassword = res && res.hasPassword === true;
                this.toasterService.pop('info', this.t('route.mail_settings'), this.t('shell.password_deleted'));
            },
            (err: any) => {
                this.saving = false;
                this.toasterService.pop('error', this.t('route.mail_settings'), this.messageOf(err, this.t('shell.delete_failed')));
            }
        );
    }

    sendTest(): void {
        if (!this.testTo) {
            this.toasterService.pop('warning', this.t('shell.test_send'), this.t('shell.enter_recipient'));
            return;
        }
        this.testing = true;
        this.dataService.send('POST', {to: this.testTo}, ActivePromotionConfig.SERVER_URL + '/rest/promotion/mail/test').subscribe(
            () => {
                this.testing = false;
                this.toasterService.pop('info', this.t('shell.test_send'), this.t('shell.test_sent'));
            },
            (err: any) => {
                this.testing = false;
                this.toasterService.pop('error', this.t('shell.test_send'), this.messageOf(err, this.t('shell.send_failed')));
            }
        );
    }

    // --- アプリ (site) ごとの公開 URL -------------------------------------

    loadSites(): void {
        this.dataService.send('GET', {}, ActivePromotionConfig.SERVER_URL + '/rest/promotion/mail/sites').subscribe(
            (res: any) => {
                this.sites = Array.isArray(res) ? res : [];
            },
            () => this.toasterService.pop('error', this.t('route.mail_settings'), this.t('shell.sites_load_failed'))
        );
    }

    saveSite(site: MailSite): void {
        this.dataService.send('PUT', site, ActivePromotionConfig.SERVER_URL + '/rest/promotion/mail/sites').subscribe(
            () => this.toasterService.pop('info', this.t('route.mail_settings'), this.t('shell.site_url_saved', {label: site.label})),
            (err: any) => this.toasterService.pop('error', this.t('route.mail_settings'), this.messageOf(err, this.t('shell.save_failed')))
        );
    }

    // --- テンプレート ----------------------------------------------------

    loadTemplates(): void {
        this.dataService.query({sql: 'SELECT FROM AP_MailTemplate ORDER BY key, site', count: 'SELECT FROM AP_MailTemplate'},
            ActivePromotionConfig.SERVER_URL).subscribe(
            (data: any) => {
                this.templates = (data.results || []).map((r: any) => ({
                    rid: r['@rid'],
                    key: r.key,
                    site: r.site || '',
                    subject: r.subject || '',
                    body: r.body || ''
                }));
            },
            () => this.toasterService.pop('error', this.t('shell.template'), this.t('shell.load_failed'))
        );
    }

    selectTemplate(row: MailTemplate): void {
        this.template = Object.assign({}, row);
        this.editing = false;
    }

    /** アプリ上書きを新規に作る。site を入れて保存すると、そのアプリだけこの本文になる */
    newTemplate(): void {
        this.template = {rid: '#-1:-1', key: 'password_reset', site: '', subject: '', body: ''};
        this.editing = true;
    }

    saveTemplate(): void {
        if (!this.template || !this.template.key) {
            this.toasterService.pop('warning', this.t('shell.template'), this.t('shell.enter_purpose_key'));
            return;
        }
        const doc: any = {
            '@class': 'AP_MailTemplate',
            '@rid': this.template.rid,
            key: this.template.key,
            site: this.template.site || '',
            subject: this.template.subject,
            body: this.template.body,
            updatedAt: new Date()
        };
        this.dataService.update([doc], ActivePromotionConfig.SERVER_URL).subscribe(
            () => {
                this.editing = false;
                this.toasterService.pop('info', this.t('shell.template'), this.t('shell.saved'));
                this.loadTemplates();
            },
            () => this.toasterService.pop('error', this.t('shell.template'), this.t('shell.save_failed'))
        );
    }

    private messageOf(err: any, fallback: string): string {
        if (err && err.error && err.error.message) {
            return err.error.message;
        }
        return fallback;
    }
}
